import React from 'react';
import { LucideIcon } from 'lucide-react';
import { cn } from '@/lib/utils';

interface Metric {
  label: string;
  value: string | number;
  icon?: LucideIcon;
  color?: string;
  trend?: {
    value: number;
    direction: 'up' | 'down' | 'neutral';
  };
}

interface MetricsGridProps {
  metrics: Metric[];
  columns?: 2 | 3 | 4;
  variant?: 'default' | 'glass' | 'minimal';
  className?: string;
}

export const MetricsGrid: React.FC<MetricsGridProps> = ({
  metrics,
  columns = 3,
  variant = 'default',
  className
}) => {
  const getMetricStyles = () => {
    switch (variant) {
      case 'glass':
        return 'bg-white/60 backdrop-blur-sm border border-white/20';
      case 'minimal':
        return 'bg-transparent border-0';
      default:
        return 'bg-muted/50 border border-border';
    }
  };

  const getTrendColor = (direction: 'up' | 'down' | 'neutral') => {
    switch (direction) {
      case 'up':
        return 'text-green-600';
      case 'down':
        return 'text-red-600';
      default:
        return 'text-muted-foreground';
    }
  };

  return (
    <div className={cn(
      `grid gap-3`,
      columns === 2 && 'grid-cols-2',
      columns === 3 && 'grid-cols-2 sm:grid-cols-3',
      columns === 4 && 'grid-cols-2 sm:grid-cols-4',
      className
    )}>
      {metrics.map((metric, index) => (
        <div 
          key={index}
          className={cn(
            "p-3 sm:p-4 rounded-lg text-center transition-all duration-200 hover:scale-105",
            getMetricStyles()
          )}
        >
          {metric.icon && (
            <div className={cn(
              "h-4 w-4 sm:h-5 sm:w-5 mx-auto mb-2",
              metric.color || 'text-primary'
            )}>
              <metric.icon className="h-full w-full" />
            </div>
          )}
          <div className="space-y-1">
            <div className="text-base sm:text-lg font-bold">{metric.value}</div>
            <div className="text-[11px] sm:text-xs text-muted-foreground">{metric.label}</div>
            {metric.trend && (
              <div className={cn(
                "text-xs font-medium",
                getTrendColor(metric.trend.direction)
              )}>
                {metric.trend.direction === 'up' ? '↗' : metric.trend.direction === 'down' ? '↘' : '→'} 
                {Math.abs(metric.trend.value)}%
              </div>
            )}
          </div>
        </div>
      ))}
    </div>
  );
};